import config from '../config/reviewsConfig';

// API service for fetching reviews from audiosum.app
class ReviewsAPI {
  constructor() {
    this.baseURL = process.env.REACT_APP_REVIEWS_API_URL || config.api.baseURL;
    this.cache = new Map();
    this.cacheTimeout = config.api.cache.timeout;
    this.timeout = config.api.timeout;
    this.debug = config.development.enableDebugLogging;
  }

  /**
   * Process API response to ensure consistent data structure
   * @param {Object} data - Raw API response
   * @returns {Object} Processed reviews data
   */
  processApiResponse(data) {
    return {
      aggregate_rating: {
        rating_value: parseFloat(data.aggregate_rating?.rating_value || 4.5),
        review_count: parseInt(data.aggregate_rating?.review_count || 0)
      },
      reviews: data.reviews?.map(review => ({
        reviewRating: {
          ratingValue: parseInt(review.reviewRating?.ratingValue || 5)
        },
        reviewBody: review.reviewBody || null,
        author: {
          name: review.author?.name || 'Anonymous'
        },
        datePublished: review.datePublished
      })) || []
    };
  }

  /**
   * Fetch reviews from audiosum.app
   * @param {Object} options - Request options
   * @param {boolean} options.useCache - Whether to use cached data
   * @param {number} options.limit - Maximum number of reviews to fetch
   * @returns {Promise<Object>} Reviews data
   */
  async fetchReviews(options = {}) {
    const { useCache = true, limit = 20 } = options;
    const cacheKey = `reviews_${limit}`;
    
    // Check cache first
    if (useCache && this.cache.has(cacheKey)) {
      const cachedData = this.cache.get(cacheKey);
      const isExpired = Date.now() - cachedData.timestamp > this.cacheTimeout;
      
      if (!isExpired) {
        if (this.debug) {
          console.log('Using cached reviews data');
        }
        return cachedData.data;
      }
    }

    try {
      const url = `${this.baseURL}/reviews?limit=${limit}`;
      
      if (this.debug) {
        console.log('🔍 ReviewsAPI Debug - Fetching reviews from:', url);
        console.log('🔍 ReviewsAPI Debug - Request config:', config.api.requestConfig);
      }
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        console.log('⏰ Request timeout after', this.timeout, 'ms');
        controller.abort();
      }, this.timeout);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
        },
        mode: 'cors',
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (this.debug) {
        console.log('🔍 ReviewsAPI Debug - Response status:', response.status, response.statusText);
        console.log('🔍 ReviewsAPI Debug - Response headers:', [...response.headers.entries()]);
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
      }

      const rawData = await response.json();
      
      if (this.debug) {
        console.log('Raw API response:', rawData);
      }
      
      // Process the API response to ensure consistent structure
      const processedData = this.processApiResponse(rawData);
      
      if (this.debug) {
        console.log('Processed reviews data:', processedData);
      }
      
      // Validate the processed response structure
      if (!this.validateReviewsData(processedData)) {
        throw new Error('Invalid reviews data structure after processing');
      }

      // Cache the successful response
      this.cache.set(cacheKey, {
        data: processedData,
        timestamp: Date.now()
      });

      return processedData;
    } catch (error) {
      if (this.debug) {
        console.log('🔍 ReviewsAPI Debug - Error details:', error);
        console.log('🔍 ReviewsAPI Debug - Error name:', error.name);
        console.log('🔍 ReviewsAPI Debug - Error message:', error.message);
        console.log('🔍 ReviewsAPI Debug - Error stack:', error.stack);
      }
      
      console.error('❌ Error fetching reviews from audiosum.app:', error);
      throw error;
    }
  }

  /**
   * Fetch aggregate rating statistics
   * @returns {Promise<Object>} Aggregate rating data
   */
  async fetchAggregateRating() {
    try {
      const url = `${this.baseURL}/reviews/stats`;
      
      if (this.debug) {
        console.log('Fetching aggregate rating from:', url);
      }
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);
      
      const response = await fetch(url, {
        method: 'GET',
        ...config.api.requestConfig,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching aggregate rating:', error);
      throw error;
    }
  }

  /**
   * Submit a new review (if needed for future functionality)
   * @param {Object} reviewData - The review data to submit
   * @returns {Promise<Object>} Submission response
   */
  async submitReview(reviewData) {
    try {
      const url = `${this.baseURL}/reviews`;
      
      const response = await fetch(url, {
        method: 'POST',
        ...config.api.requestConfig,
        body: JSON.stringify(reviewData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      
      // Clear cache after successful submission
      this.cache.clear();
      
      return data;
    } catch (error) {
      console.error('Error submitting review:', error);
      throw error;
    }
  }

  /**
   * Validate the structure of reviews data
   * @param {Object} data - The data to validate
   * @returns {boolean} True if valid, false otherwise
   */
  validateReviewsData(data) {
    if (!data || typeof data !== 'object') {
      return false;
    }

    // Check if it has the expected structure
    if (data.reviews && Array.isArray(data.reviews)) {
      // Validate each review
      return data.reviews.every(review => 
        review.reviewRating && 
        review.author && 
        review.author.name &&
        typeof review.reviewRating.ratingValue !== 'undefined'
      );
    }

    return false;
  }

  /**
   * Get fallback reviews data (for when API is unavailable)
   * @param {string} language - Language code ('en' or 'ar')
   * @returns {Object} Fallback reviews data
   */
  getFallbackReviews(language = 'en') {
    const fallbackData = {
      en: {
        aggregate_rating: {
          rating_value: 4.8,
          review_count: 150
        },
        reviews: [
          {
            reviewRating: { ratingValue: 5 },
            reviewBody: "Excellent transcription service with incredibly high accuracy. Highly recommended!",
            author: { name: 'Ahmed Mohammed' },
            datePublished: new Date().toISOString()
          },
          {
            reviewRating: { ratingValue: 5 },
            reviewBody: "Amazing tool for summarizing lectures and meetings. Saved me so much time.",
            author: { name: 'Sarah Ahmed' },
            datePublished: new Date().toISOString()
          },
          {
            reviewRating: { ratingValue: 4 },
            reviewBody: "Easy to use and very fast. Results are accurate and helpful.",
            author: { name: 'Mohammed Ali' },
            datePublished: new Date().toISOString()
          },
          {
            reviewRating: { ratingValue: 5 },
            reviewBody: "Best summarization tool I've tried. Arabic language support is excellent.",
            author: { name: 'Fatima Hassan' },
            datePublished: new Date().toISOString()
          },
          {
            reviewRating: { ratingValue: 5 },
            reviewBody: "Perfect for students and researchers. Helps a lot in reviewing lectures.",
            author: { name: 'Omar Salem' },
            datePublished: new Date().toISOString()
          },
          {
            reviewRating: { ratingValue: 4 },
            reviewBody: "Professional and reliable service. I use it daily in my work.",
            author: { name: 'Layla Karim' },
            datePublished: new Date().toISOString()
          }
        ]
      },
      ar: {
        aggregate_rating: {
          rating_value: 4.8,
          review_count: 150
        },
        reviews: [
          {
            reviewRating: { ratingValue: 5 },
            reviewBody: "خدمة ممتازة للتحويل الصوتي بدقة عالية جداً. أنصح بها بشدة!",
            author: { name: 'أحمد محمد' },
            datePublished: new Date().toISOString()
          },
          {
            reviewRating: { ratingValue: 5 },
            reviewBody: "أداة رائعة لتلخيص المحاضرات والاجتماعات. وفرت علي وقت كثير.",
            author: { name: 'سارة أحمد' },
            datePublished: new Date().toISOString()
          },
          {
            reviewRating: { ratingValue: 4 },
            reviewBody: "سهلة الاستخدام وسريعة جداً. النتائج دقيقة ومفيدة.",
            author: { name: 'محمد علي' },
            datePublished: new Date().toISOString()
          },
          {
            reviewRating: { ratingValue: 5 },
            reviewBody: "أفضل أداة تلخيص جربتها. دعم اللغة العربية ممتاز.",
            author: { name: 'فاطمة حسن' },
            datePublished: new Date().toISOString()
          },
          {
            reviewRating: { ratingValue: 5 },
            reviewBody: "مثالية للطلاب والباحثين. تساعد كثيراً في مراجعة المحاضرات.",
            author: { name: 'عمر سالم' },
            datePublished: new Date().toISOString()
          },
          {
            reviewRating: { ratingValue: 4 },
            reviewBody: "خدمة احترافية وموثوقة. أستخدمها يومياً في عملي.",
            author: { name: 'ليلى كريم' },
            datePublished: new Date().toISOString()
          }
        ]
      }
    };

    return fallbackData[language] || fallbackData.en;
  }

  /**
   * Clear the reviews cache
   */
  clearCache() {
    this.cache.clear();
  }

  /**
   * Set custom base URL (useful for development/testing)
   * @param {string} url - The new base URL
   */
  setBaseURL(url) {
    this.baseURL = url;
  }
}

// Create and export a singleton instance
const reviewsAPI = new ReviewsAPI();

export default reviewsAPI;